/**
 * Dominios principales de la app (estilo Shopify).
 *
 * - smarty.lat / www.smarty.lat → Marketing (landing)
 * - web.smarty.lat → Panel de mercaderes (dashboard, POS, configuración) — donde entran tus clientes
 * - admin.smarty.lat → Solo super admin (gestión de tenants, impersonar) — uso interno
 * - {tenant}.smarty.lat → Ecommerce público del tenant
 */

/** Dominio raíz en producción (marketing + panel + super admin). */
export const ROOT_DOMAIN_PROD = 'smarty.lat';

/** Dominio raíz en desarrollo local. */
export const ROOT_DOMAIN_DEV = 'smarty.local';

/**
 * Dominio raíz de las TIENDAS (storefront), SEPARADO del corporativo.
 *
 * Las tiendas viven en Cloudflare Workers bajo `smartylat.com` (dominio
 * DEDICADO): `<slug>.smartylat.com`. El admin/marketing/super-admin siguen en
 * `smarty.lat`. No se mezclan a propósito — un comodín sobre el corporativo se
 * comería `media.`/`web.` y filtraría cookies al panel. Ver
 * docs/adr/0001-ruteo-de-storefronts-por-tenant.md
 *
 * NO tiene variante `.local`: la tienda se sirve desde Cloudflare, no desde el
 * dev server del admin. Un link "ver tienda" SIEMPRE va a producción.
 */
export const STOREFRONT_ROOT_DOMAIN = 'smartylat.com';

/**
 * El CNAME que pega un merchant para conectar SU dominio (fallback origin de
 * Cloudflare for SaaS). Espejo frontend de `FALLBACK_ORIGIN` en
 * `supabase/functions/shared/customHostname.ts` — el backend es la fuente de
 * verdad (manda este valor en `dns_instructions`); el frontend solo lo usa como
 * fallback de presentación cuando aún no consultó al backend.
 */
export const STOREFRONT_CNAME_TARGET = `shops.${STOREFRONT_ROOT_DOMAIN}`;

/** Host del panel de mercaderes en producción (dashboard, POS, login). */
export const WEB_HOST_PROD = 'web.smarty.lat';

/** Host del panel de mercaderes en desarrollo. */
export const WEB_HOST_DEV = 'web.smarty.local';

/** Host del super admin en producción (solo gestión interna). */
export const ADMIN_HOST_PROD = 'admin.smarty.lat';

/** Host del super admin en desarrollo. */
export const ADMIN_HOST_DEV = 'admin.smarty.local';

/** Dominios reservados que no son tiendas (subdominios que no se usan como tenant). */
export const RESERVED_SUBDOMAINS = ['admin', 'web', 'www', 'app', 'api', 'mail'] as const;

export function isReservedSubdomain(sub: string): boolean {
  return RESERVED_SUBDOMAINS.includes(sub.toLowerCase() as (typeof RESERVED_SUBDOMAINS)[number]);
}

/** True si estamos en entorno de desarrollo (localhost, 127.0.0.1 o cualquier .smarty.local). */
export function isDevelopmentHost(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.smarty.local');
}

/** Host del panel de mercaderes (web): .local en desarrollo, .lat en producción. */
export function getWebHost(): string {
  return isDevelopmentHost() ? WEB_HOST_DEV : WEB_HOST_PROD;
}

/** Host del super admin: .local en desarrollo, .lat en producción. */
export function getAdminHost(): string {
  return isDevelopmentHost() ? ADMIN_HOST_DEV : ADMIN_HOST_PROD;
}

/** Dominio raíz según entorno: .local en desarrollo, .lat en producción. */
export function getRootDomain(): string {
  return isDevelopmentHost() ? ROOT_DOMAIN_DEV : ROOT_DOMAIN_PROD;
}
